import React, { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { apiTokenService } from '@/services/apiTokenService'

const EXPIRY_OPTIONS = [30, 60, 90, 180, 365]

const CreateTokenDialog = ({ open, onOpenChange, meta, onCreated }) => {
  // Only what the admin's AI access policy lets this user's role give a token
  const allowed = new Set(meta?.policy?.allowed_scopes || (meta?.scopes || []).map(s => s.id))
  const presets = (meta?.presets || []).filter(p => p.scopes.every(s => allowed.has(s)))
  const scopes = (meta?.scopes || []).filter(s => allowed.has(s.id))
  const maxDays = meta?.max_expiry_days || 365
  const expiryOptions = [...new Set([...EXPIRY_OPTIONS.filter(d => d < maxDays), maxDays])]
  const defaultExpiry = String(expiryOptions.filter(d => d <= (meta?.default_expiry_days || 90)).pop() || expiryOptions[0] || maxDays)
  const [name, setName] = useState('')
  const [preset, setPreset] = useState('agent')
  const [customScopes, setCustomScopes] = useState([])
  const [expiry, setExpiry] = useState('90')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (open) {
      setName('')
      setPreset(presets[0]?.id || 'custom')
      setCustomScopes(presets[0]?.scopes || [...allowed])
      setExpiry(defaultExpiry)
      setPassword('')
    }
  }, [open])

  const selectedScopes = preset === 'custom'
    ? customScopes
    : presets.find(p => p.id === preset)?.scopes || []

  const toggleScope = (scope, checked) => {
    setCustomScopes(prev => checked ? [...new Set([...prev, scope])] : prev.filter(s => s !== scope))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!name.trim()) return toast.error('Give the token a name')
    if (!selectedScopes.length) return toast.error('Select at least one permission')
    if (!password) return toast.error('Enter your current password')

    setLoading(true)
    try {
      const res = await apiTokenService.create({
        name: name.trim(),
        scopes: selectedScopes,
        expires_in_days: Number(expiry),
        current_password: password,
      })
      onCreated(res.data.token, res.data.api_token)
      onOpenChange(false)
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create token')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>Create API token</DialogTitle>
            <DialogDescription>
              A token lets an AI tool or script act as you. It can never do more than you can in the app.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-1.5">
            <Label htmlFor="token-name">Name</Label>
            <Input
              id="token-name"
              placeholder="e.g. Claude Code – work laptop"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
              autoFocus
            />
          </div>

          <div className="space-y-1.5">
            <Label>Permissions</Label>
            <div className="space-y-2">
              {[...presets, { id: 'custom', label: 'Custom', description: 'Pick exactly which permissions this token gets.' }].map(p => (
                <button
                  type="button"
                  key={p.id}
                  onClick={() => setPreset(p.id)}
                  className={cn(
                    'w-full text-left rounded-md border px-3 py-2 transition-colors',
                    preset === p.id ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
                  )}
                >
                  <div className="text-sm font-medium">{p.label}</div>
                  <div className="text-xs text-muted-foreground">{p.description}</div>
                </button>
              ))}
            </div>
            {preset === 'custom' && (
              <div className="grid gap-2 rounded-md border p-3 mt-2">
                {scopes.map(s => (
                  <label key={s.id} className="flex items-start gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={customScopes.includes(s.id)}
                      onCheckedChange={(checked) => toggleScope(s.id, !!checked)}
                      className="mt-0.5"
                    />
                    <span>
                      <span className="font-medium">{s.label}</span>{' '}
                      <code className="text-[11px] text-muted-foreground">{s.id}</code>
                      <span className="block text-xs text-muted-foreground">{s.description}</span>
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>Expires after</Label>
            <Select value={expiry} onValueChange={setExpiry}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {expiryOptions.map(d => (
                  <SelectItem key={d} value={String(d)}>{d} days</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="token-password">Confirm your password</Label>
            <Input
              id="token-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create token
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default CreateTokenDialog
